// pages/ReleaseManagement.js
// Release Management — read-only observability over the GaliGali app fleet.
// Two tabs:
//   • Fleet Versions — which devices run which version of each first-party app
//     (agent / player / handover), powered by the ggos_device_apps inventory.
//   • APK Library   — the catalogue of uploaded APK artifacts.
// v1 is deliberately read-only for rollout; installs still happen per-device
// from the device command panel. Bulk rollout is a later iteration.

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Rocket, GitBranch, Package, RefreshCw, ChevronRight, ChevronDown,
  Smartphone, Wifi, WifiOff, HelpCircle, Clock, Archive, Search,
  Layers, AlertTriangle, Download, Check,
  Hammer, Plus, X, GitCommit, ExternalLink, CheckCircle2, XCircle, Loader2,
} from 'lucide-react';
import { ggosDeviceService } from '../services/api';
import './ReleaseManagement.css';

const VERSION_COLORS = ['#4f8cff', '#22c55e', '#f59e0b', '#a855f7', '#ec4899', '#14b8a6', '#ef4444', '#64748b'];

const fmtBytes = (b) => {
  if (b == null) return '—';
  const n = Number(b);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const fmtRel = (ts) => {
  if (!ts) return 'never';
  const ms = Date.now() - new Date(ts).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(ms / 3600000);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(ms / 86400000)}d ago`;
};

const fmtDate = (ts) =>
  ts ? new Date(ts).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// Friendly label for a package name.
const APP_LABELS = {
  'com.galigali.enterprise.agent': 'Enterprise Agent',
  'com.galigali.enterprise.agent.debug': 'Enterprise Agent (debug)',
  'com.galigali.enterprise.player': 'Enterprise Player',
  'com.galigali.enterprise.player.debug': 'Enterprise Player (debug)',
  'live.galigali.handover.driver': 'Handover — Driver',
  'live.galigali.handover.supervisor': 'Handover — Supervisor',
};
const appLabel = (pkg, fallback) => APP_LABELS[pkg] || fallback || pkg;
const isDebugPkg = (pkg) => pkg.endsWith('.debug');

const CONN = {
  online: { icon: Wifi, cls: 'online', label: 'Online' },
  offline: { icon: WifiOff, cls: 'offline', label: 'Offline' },
  unknown: { icon: HelpCircle, cls: 'unknown', label: 'Unknown' },
};

// ── Per-version device drill-down (lazy) ──
const VersionDevices = ({ packageName, versionName }) => {
  const [devices, setDevices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    ggosDeviceService
      .getAppVersionDevices(packageName, versionName)
      .then((res) => { if (alive) setDevices(res.data || []); })
      .catch(() => { if (alive) setError('Failed to load devices'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [packageName, versionName]);

  if (loading) return <div className="rm-drill-loading"><RefreshCw size={14} className="rm-spin" /> Loading devices…</div>;
  if (error) return <div className="rm-drill-error">{error}</div>;
  if (!devices || devices.length === 0) return <div className="rm-drill-empty">No devices.</div>;

  return (
    <div className="rm-drill">
      <table className="rm-drill-table">
        <thead>
          <tr>
            <th>Device</th><th>Connection</th><th>Last seen</th><th>Reported</th>
          </tr>
        </thead>
        <tbody>
          {devices.map((d) => {
            const conn = CONN[d.connectionStatus] || CONN.unknown;
            const ConnIcon = conn.icon;
            return (
              <tr key={d.thingName}>
                <td>
                  <Link to={`/ggos-devices/${d.thingName}`} className="rm-device-link">
                    <Smartphone size={13} /> {d.thingName}
                  </Link>
                  {d.isTestDevice && <span className="rm-tag rm-tag--test">test</span>}
                  {!d.isActive && <span className="rm-tag rm-tag--gray">inactive</span>}
                </td>
                <td><span className={`rm-conn rm-conn--${conn.cls}`}><ConnIcon size={12} /> {conn.label}</span></td>
                <td>{fmtRel(d.lastConnectedAt)}</td>
                <td>{fmtRel(d.syncedAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── One app package card with a version breakdown ──
const PackageCard = ({ pkg }) => {
  const [openVersion, setOpenVersion] = useState(null);
  const total = pkg.totalDevices || 0;

  return (
    <div className={`rm-pkg ${isDebugPkg(pkg.packageName) ? 'rm-pkg--debug' : ''}`}>
      <div className="rm-pkg-head">
        <div className="rm-pkg-title">
          <Smartphone size={16} />
          <div>
            <div className="rm-pkg-name">
              {appLabel(pkg.packageName, pkg.appName)}
              {isDebugPkg(pkg.packageName) && <span className="rm-tag rm-tag--amber">debug</span>}
            </div>
            <code className="rm-pkg-pkg">{pkg.packageName}</code>
          </div>
        </div>
        <div className="rm-pkg-stats">
          <div className="rm-pkg-stat"><span>{total}</span> device{total === 1 ? '' : 's'}</div>
          <div className="rm-pkg-stat"><span>{pkg.versionCount}</span> version{pkg.versionCount === 1 ? '' : 's'}</div>
        </div>
      </div>

      {/* Adoption bar */}
      <div className="rm-adopt" title="Version distribution across the fleet">
        {pkg.versions.map((v, i) => {
          const pct = total > 0 ? (v.deviceCount / total) * 100 : 0;
          return (
            <div
              key={`${v.versionName}-${i}`}
              className="rm-adopt-seg"
              style={{ width: `${pct}%`, background: VERSION_COLORS[i % VERSION_COLORS.length] }}
              title={`${v.versionName || 'unknown'} · ${v.deviceCount} (${pct.toFixed(0)}%)`}
            />
          );
        })}
      </div>

      <table className="rm-ver-table">
        <thead>
          <tr>
            <th></th><th>Version</th><th>Code</th><th>Devices</th><th>Adoption</th><th>Last reported</th><th></th>
          </tr>
        </thead>
        <tbody>
          {pkg.versions.map((v, i) => {
            const pct = total > 0 ? (v.deviceCount / total) * 100 : 0;
            const key = v.versionName || `__null_${i}`;
            const isOpen = openVersion === key;
            return (
              <React.Fragment key={key}>
                <tr className={`rm-ver-row ${isOpen ? 'rm-ver-row--open' : ''}`} onClick={() => setOpenVersion(isOpen ? null : key)}>
                  <td className="rm-ver-chev">{isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</td>
                  <td>
                    <span className="rm-ver-dot" style={{ background: VERSION_COLORS[i % VERSION_COLORS.length] }} />
                    <strong>{v.versionName || <em className="rm-muted">unknown</em>}</strong>
                  </td>
                  <td className="rm-muted">{v.versionCode ?? '—'}</td>
                  <td>{v.deviceCount}</td>
                  <td>
                    <div className="rm-mini-bar"><div style={{ width: `${pct}%`, background: VERSION_COLORS[i % VERSION_COLORS.length] }} /></div>
                    <span className="rm-pct">{pct.toFixed(0)}%</span>
                  </td>
                  <td className="rm-muted"><Clock size={12} /> {fmtRel(v.latestSyncedAt)}</td>
                  <td className="rm-ver-cta">{isOpen ? 'Hide' : 'Devices'}</td>
                </tr>
                {isOpen && (
                  <tr className="rm-drill-row">
                    <td colSpan={7}><VersionDevices packageName={pkg.packageName} versionName={v.versionName} /></td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Fleet Versions tab ──
const FleetVersions = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ggosDeviceService.getFleetAppVersions();
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load fleet versions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="rm-loading"><RefreshCw size={20} className="rm-spin" /> Loading fleet versions…</div>;
  if (error) return <div className="rm-error"><AlertTriangle size={18} /> {error}</div>;

  const packages = data?.packages || [];

  return (
    <div className="rm-fleet">
      <div className="rm-summary">
        <div className="rm-summary-card">
          <Layers size={18} />
          <div><span className="rm-summary-num">{packages.length}</span><span className="rm-summary-lbl">Apps tracked</span></div>
        </div>
        <div className="rm-summary-card">
          <Smartphone size={18} />
          <div><span className="rm-summary-num">{data?.totalDevices ?? 0}</span><span className="rm-summary-lbl">Devices reporting</span></div>
        </div>
        <div className="rm-summary-card rm-summary-card--muted">
          <Clock size={18} />
          <div><span className="rm-summary-num">{fmtRel(data?.generatedAt)}</span><span className="rm-summary-lbl">Generated</span></div>
        </div>
        <button className="rm-refresh" onClick={load}><RefreshCw size={14} /> Refresh</button>
      </div>

      {packages.length === 0 ? (
        <div className="rm-empty">No GaliGali apps reported yet. Devices populate this after their next <code>get_installed_packages</code> sync.</div>
      ) : (
        <div className="rm-pkg-list">
          {packages.map((pkg) => <PackageCard key={pkg.packageName} pkg={pkg} />)}
        </div>
      )}
    </div>
  );
};

// ── APK Library tab ──
const ApkLibrary = () => {
  const [apks, setApks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ggosDeviceService.getApkLibrary({ search, showArchived: String(showArchived) });
      setApks(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load APK library');
    } finally {
      setLoading(false);
    }
  }, [search, showArchived]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const copyDownloadUrl = async (apk) => {
    try {
      const res = await ggosDeviceService.getApkDownloadUrl(apk.id);
      const url = res.data?.downloadUrl;
      if (url) {
        await navigator.clipboard.writeText(url);
        setCopiedId(apk.id);
        setTimeout(() => setCopiedId(null), 1800);
      }
    } catch { /* ignore */ }
  };

  return (
    <div className="rm-apks">
      <div className="rm-apks-toolbar">
        <div className="rm-search">
          <Search size={15} />
          <input placeholder="Search file, label or package…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <label className="rm-checkbox">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          Show archived
        </label>
        <button className="rm-refresh" onClick={load}><RefreshCw size={14} /> Refresh</button>
      </div>

      {loading ? (
        <div className="rm-loading"><RefreshCw size={20} className="rm-spin" /> Loading library…</div>
      ) : error ? (
        <div className="rm-error"><AlertTriangle size={18} /> {error}</div>
      ) : apks.length === 0 ? (
        <div className="rm-empty">No APKs in the library.</div>
      ) : (
        <table className="rm-apk-table">
          <thead>
            <tr>
              <th>APK</th><th>Package</th><th>Version</th><th>Size</th><th>Installs</th><th>Uploaded</th><th></th>
            </tr>
          </thead>
          <tbody>
            {apks.map((apk) => (
              <tr key={apk.id} className={apk.is_archived ? 'rm-apk-archived' : ''}>
                <td>
                  <div className="rm-apk-name">
                    <Package size={14} />
                    <span>{apk.label || apk.file_name}</span>
                    {apk.is_archived && <span className="rm-tag rm-tag--gray">archived</span>}
                  </div>
                  {apk.label && <div className="rm-apk-file">{apk.file_name}</div>}
                </td>
                <td>{apk.package_name ? <code className="rm-pkg-pkg">{apk.package_name}</code> : <span className="rm-muted">unidentified</span>}</td>
                <td>{apk.version_name || <span className="rm-muted">—</span>}</td>
                <td className="rm-muted">{fmtBytes(apk.file_size)}</td>
                <td>{apk.install_count || 0}</td>
                <td className="rm-muted">
                  <div>{fmtDate(apk.uploaded_at)}</div>
                  <div className="rm-apk-by">{apk.uploaded_by}</div>
                </td>
                <td>
                  <button className="rm-icon-btn" title="Copy download URL" onClick={() => copyDownloadUrl(apk)}>
                    {copiedId === apk.id ? <Check size={15} /> : <Download size={15} />}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const BUILD_APPS = [
  { id: 'enterprise-agent', label: 'Enterprise Agent' },
  { id: 'enterprise-player', label: 'Enterprise Player' },
];

const BUILD_STATUS = {
  queued:   { cls: 'gray',  label: 'Queued',   icon: Clock },
  building: { cls: 'blue',  label: 'Building',  icon: Loader2, spin: true },
  success:  { cls: 'green', label: 'Success',   icon: CheckCircle2 },
  failed:   { cls: 'red',   label: 'Failed',    icon: XCircle },
  canceled: { cls: 'gray',  label: 'Canceled',  icon: XCircle },
};

const fmtDateTime = (ts) =>
  ts ? new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

// ── Build APK modal ──
const BuildApkModal = ({ onClose, onTriggered, onError }) => {
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState('');
  const [apps, setApps] = useState(BUILD_APPS.map((a) => a.id));
  const [buildType, setBuildType] = useState('debug');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    ggosDeviceService.getRepoBranches()
      .then((res) => {
        const list = res.data?.branches || [];
        setBranches(list);
        setBranch((b) => b || list[0] || '');
      })
      .catch(() => {});
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const toggleApp = (id) =>
    setApps((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (!branch.trim()) { onError('Pick a branch'); return; }
    if (apps.length === 0) { onError('Select at least one app'); return; }
    setSubmitting(true);
    try {
      const res = await ggosDeviceService.triggerApkBuild({ branch: branch.trim(), apps, buildType });
      onTriggered(res.data);
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to start build');
      setSubmitting(false);
    }
  };

  return (
    <div className="rm-modal-overlay" onClick={onClose}>
      <div className="rm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rm-modal-head">
          <h2><Hammer size={18} /> Build APK</h2>
          <button className="rm-modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="rm-modal-body">
            <label className="rm-field">
              <span className="rm-field-label">Branch</span>
              <input
                list="rm-branch-list"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                placeholder="e.g. main"
                autoFocus
              />
              <datalist id="rm-branch-list">
                {branches.map((b) => <option key={b} value={b} />)}
              </datalist>
              <span className="rm-field-help">Pick from the list or type any branch name.</span>
            </label>

            <div className="rm-field">
              <span className="rm-field-label">Apps</span>
              <div className="rm-app-choices">
                {BUILD_APPS.map((a) => (
                  <label key={a.id} className={`rm-app-choice ${apps.includes(a.id) ? 'rm-app-choice--on' : ''}`}>
                    <input type="checkbox" checked={apps.includes(a.id)} onChange={() => toggleApp(a.id)} />
                    <Smartphone size={14} /> {a.label}
                  </label>
                ))}
              </div>
              <span className="rm-field-help">Agent + player are co-signed with the same keystore — build both for a coherent release.</span>
            </div>

            <div className="rm-field">
              <span className="rm-field-label">Build type</span>
              <div className="rm-radio-row">
                {['debug', 'release'].map((t) => (
                  <label key={t} className={`rm-radio ${buildType === t ? 'rm-radio--on' : ''}`}>
                    <input type="radio" name="buildType" checked={buildType === t} onChange={() => setBuildType(t)} />
                    {t}
                  </label>
                ))}
              </div>
              <span className="rm-field-help">Both types currently sign with the debug keystore.</span>
            </div>
          </div>
          <div className="rm-modal-foot">
            <button type="button" className="rm-btn rm-btn--ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="rm-btn rm-btn--primary" disabled={submitting}>
              {submitting ? <><Loader2 size={14} className="rm-spin" /> Starting…</> : <><Hammer size={14} /> Start build</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Builds tab ──
const Builds = () => {
  const [builds, setBuilds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await ggosDeviceService.getApkBuilds();
      setBuilds(res.data || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load builds');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll while any build is still in flight.
  useEffect(() => {
    const active = builds.some((b) => b.status === 'queued' || b.status === 'building');
    if (!active) return undefined;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [builds, load]);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3500); };

  return (
    <div className="rm-builds">
      {toast && <div className="rm-toast">{toast}</div>}
      <div className="rm-builds-toolbar">
        <p className="rm-builds-note">
          Builds run in the <code>build-android-apk</code> GitHub Actions workflow and land in the APK Library on success.
        </p>
        <button className="rm-refresh" onClick={load}><RefreshCw size={14} /> Refresh</button>
        <button className="rm-btn rm-btn--primary" onClick={() => setModalOpen(true)}><Plus size={15} /> Build APK</button>
      </div>

      {loading ? (
        <div className="rm-loading"><RefreshCw size={20} className="rm-spin" /> Loading builds…</div>
      ) : error ? (
        <div className="rm-error"><AlertTriangle size={18} /> {error}</div>
      ) : builds.length === 0 ? (
        <div className="rm-empty">No builds yet. Click <strong>Build APK</strong> to compile a branch.</div>
      ) : (
        <div className="rm-build-list">
          {builds.map((b) => {
            const st = BUILD_STATUS[b.status] || BUILD_STATUS.queued;
            const StIcon = st.icon;
            return (
              <div key={b.id} className="rm-build">
                <div className={`rm-build-status rm-build-status--${st.cls}`}>
                  <StIcon size={15} className={st.spin ? 'rm-spin' : ''} /> {st.label}
                </div>
                <div className="rm-build-main">
                  <div className="rm-build-title">
                    <GitBranch size={13} /> <strong>{b.branch}</strong>
                    {b.gitCommit && <span className="rm-build-commit"><GitCommit size={12} /> {b.gitCommit.slice(0, 8)}</span>}
                    <span className={`rm-tag rm-tag--${b.buildType === 'release' ? 'blue' : 'amber'}`}>{b.buildType}</span>
                  </div>
                  <div className="rm-build-meta">
                    {b.apps?.join(' + ')} · by {b.triggeredBy} · {fmtDateTime(b.createdAt)}
                    {b.apkCount > 0 && <> · <strong>{b.apkCount}</strong> APK{b.apkCount === 1 ? '' : 's'}</>}
                  </div>
                  {b.errorMessage && <div className="rm-build-error">{b.errorMessage}</div>}
                </div>
                {b.logsUrl && (
                  <a className="rm-build-logs" href={b.logsUrl} target="_blank" rel="noreferrer">
                    Logs <ExternalLink size={12} />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <BuildApkModal
          onClose={() => setModalOpen(false)}
          onError={flash}
          onTriggered={() => { setModalOpen(false); flash('Build started'); load(); }}
        />
      )}
    </div>
  );
};

// ── Shell ──
const ReleaseManagement = ({ tab = 'fleet' }) => {
  return (
    <div className="rm-page">
      <div className="rm-header">
        <div className="rm-header-title">
          <Rocket size={22} />
          <div>
            <h1>Release Management</h1>
            <p>Versioned GaliGali app artifacts and live fleet adoption.</p>
          </div>
        </div>
      </div>

      <div className="rm-tabs">
        <Link to="/releases/fleet" className={`rm-tab ${tab === 'fleet' ? 'rm-tab--active' : ''}`}>
          <GitBranch size={15} /> Fleet Versions
        </Link>
        <Link to="/releases/apks" className={`rm-tab ${tab === 'apks' ? 'rm-tab--active' : ''}`}>
          <Archive size={15} /> APK Library
        </Link>
        <Link to="/releases/builds" className={`rm-tab ${tab === 'builds' ? 'rm-tab--active' : ''}`}>
          <Hammer size={15} /> Builds
        </Link>
      </div>

      {tab === 'apks' ? <ApkLibrary /> : tab === 'builds' ? <Builds /> : <FleetVersions />}
    </div>
  );
};

export default ReleaseManagement;
